import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Badge,
  Tooltip,
  Stack,
} from "@altimateai/lego";
import { IconInfoCircle, IconAlertTriangle } from "@tabler/icons-react";
import { Privacy } from "@/helpers/constants";
import { KnowledgeCatalogItem, KnowledgeBaseDetailResponse } from "../types";
import PrivateLinkSection from "./PrivateLinkSection";
import {
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  isFailedStatus,
  toUserFriendlyError,
} from "../utils/statusHelpers";

interface MetadataCardProps {
  data?: KnowledgeCatalogItem | KnowledgeBaseDetailResponse;
  createdDate: string;
  getUserInfo: (userId?: number) => string | null;
  templateName?: string;
  onLinkCopied?: () => void;
}

export default function MetadataCard({
  data,
  createdDate,
  getUserInfo,
  templateName,
  onLinkCopied,
}: MetadataCardProps) {
  return (
    <Card>
      <CardHeader>
        <Stack direction="row" align="center" gap="sm">
          <IconInfoCircle className="al-w-5 al-h-5 al-text-gray-500" />
          <Typography variant="h4" weight="medium">
            Metadata
          </Typography>
        </Stack>
      </CardHeader>
      <CardContent>
        <Stack direction="column" gap="lg">
          {/* First Row - Main metadata */}
          <Stack direction="row" gap="xl" className="al-flex-wrap">
            {/* Processing Status - Only show for V2 knowledge bases with documents */}
            {"processing_status" in (data || {}) && (data as KnowledgeBaseDetailResponse)?.processing_status && (
              <Stack direction="column" gap="xs">
                <Typography variant="body" size="sm" className="al-text-gray-600">
                  Status:
                </Typography>
                <Stack direction="row" align="center" gap="sm">
                  <Badge variant={getStatusColor((data as KnowledgeBaseDetailResponse).processing_status)}>
                    <Stack direction="row" align="center" gap="xs">
                      {getStatusIcon((data as KnowledgeBaseDetailResponse).processing_status)}
                      {getStatusLabel((data as KnowledgeBaseDetailResponse).processing_status)}
                    </Stack>
                  </Badge>
                  {isFailedStatus((data as KnowledgeBaseDetailResponse).processing_status) && (
                      <Tooltip title={toUserFriendlyError((data as KnowledgeBaseDetailResponse).processing_error)}>
                        <span>
                          <IconAlertTriangle className="al-w-4 al-h-4 al-text-destructive al-cursor-help" />
                        </span>
                      </Tooltip>
                    )}
                </Stack>
              </Stack>
            )}

            {/* Visibility */}
            <Stack direction="column" gap="xs">
              <Typography variant="body" size="sm" className="al-text-gray-600">
                Visibility:
              </Typography>
              <Typography variant="body" size="sm">
                {data?.privacy === Privacy.PUBLIC ? "Public" : "Private"}
              </Typography>
            </Stack>

            {/* Created on */}
            <Stack direction="column" gap="xs">
              <Typography variant="body" size="sm" className="al-text-gray-600">
                Created on:
              </Typography>
              <Typography variant="body" size="sm">{createdDate}</Typography>
            </Stack>

            {/* Created by - Only show if user is found (not a service user) */}
            {data?.user_id && getUserInfo(data.user_id) && (
              <Stack direction="column" gap="xs">
                <Typography variant="body" size="sm" className="al-text-gray-600">
                  Created by:
                </Typography>
                <Typography variant="body" size="sm">{getUserInfo(data.user_id)}</Typography>
              </Stack>
            )}
          </Stack>

          {/* Description */}
          {!templateName && (
            <Stack direction="column" gap="xs">
              <Typography variant="body" size="sm" className="al-text-gray-600">
                Description:
              </Typography>
              <Typography variant="body" size="sm">
                {data?.description || "No description provided."}
              </Typography>
            </Stack>
          )}

          {/* Link Section */}
          {!templateName && <PrivateLinkSection publicId={data?.public_id} onCopy={onLinkCopied} />}
        </Stack>
      </CardContent>
    </Card>
  );
}
